import { z } from 'zod';

/**
 * Accessibility preferences — the shared contract layer for the narrow backend
 * surface of B41.
 *
 * `FR-EXP-018` names the per-user opt-out from single-key shortcuts, and
 * `FR-EXP-023` names the per-user reduced-motion override. Both values already
 * live on `user_preferences`; this file describes only the two booleans the
 * accessibility routes expose, not the table as a whole.
 *
 * The read shape is a view and therefore a plain `z.object`, like other shared
 * response contracts. The patch shapes are field-scoped `z.strictObject`s:
 * each PATCH route may touch exactly one column, and an unrelated preference
 * key must be a validation failure rather than a silently stripped field.
 */

/** `GET /me/accessibility-preferences` — both B41 accessibility booleans. */
export const accessibilityPreferencesSchema = z.object({
  singleKeyShortcutsDisabled: z.boolean(),
  reducedMotionOverride: z.boolean(),
});

export type AccessibilityPreferences = z.infer<typeof accessibilityPreferencesSchema>;

/** `PATCH /me/accessibility-preferences/single-key-shortcuts`. */
export const singleKeyShortcutsPatchSchema = z.strictObject({
  singleKeyShortcutsDisabled: z.boolean(),
});

export type SingleKeyShortcutsPatch = z.infer<typeof singleKeyShortcutsPatchSchema>;

/** `PATCH /me/accessibility-preferences/reduced-motion`. */
export const reducedMotionPatchSchema = z.strictObject({
  reducedMotionOverride: z.boolean(),
});

export type ReducedMotionPatch = z.infer<typeof reducedMotionPatchSchema>;
